/**
 * Shared utilities and base sub-components for analytics views.
 * Used by both AnalyticsView (owner/admin) and SalesPersonAnalyticsView.
 */
import React, { useState } from 'react';
import CustomDatePickerComponent from '../shared/CustomDatePicker';
import {
    ChevronDown, ChevronUp, AlertTriangle, Package, Calendar, Users,
    ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';

// ─── Utilities ───────────────────────────────────────────────────────────────

export function formatCurrency(value) {
    if (value == null || value === '') return 'AED 0';
    const num = Number(value) || 0;
    return `AED ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num)}`;
}

export function formatCardAmount(value) {
    if (value == null || value === '') return { currency: 'AED', exact: '0', compact: null, full: 'AED 0' };
    
    let num = 0;
    if (typeof value === 'number') {
        num = value;
    } else {
        const clean = String(value).replace(/[^0-9.-]+/g, '');
        num = Number(clean) || 0;
    }

    const abs = Math.abs(num);
    const formattedExact = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(num);

    let compactStr = null;
    if (abs >= 1_000_000_000) {
        compactStr = `${(num / 1_000_000_000).toFixed(2)}B`;
    } else if (abs >= 1_000_000) {
        compactStr = `${(num / 1_000_000).toFixed(2)}M`;
    }

    return {
        currency: 'AED',
        exact: formattedExact,
        compact: compactStr,
        full: `AED ${formattedExact}`
    };
}

export function formatNumber(value) {
    if (value == null) return '0';
    return new Intl.NumberFormat('en-US').format(Number(value) || 0);
}

export function getAvatarGradient(name = '') {
    const palette = [
        'from-violet-500 to-purple-600', 'from-blue-500 to-cyan-500',
        'from-emerald-500 to-teal-500', 'from-orange-500 to-amber-500',
        'from-rose-500 to-pink-500', 'from-sky-500 to-indigo-500',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
}

/** Build date range from a preset filter key */
export function buildDateRange(filter) {
    const now = new Date();
    const endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);

    let startDate;
    if (filter === 'today') {
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
    } else if (filter === 'last7') {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
    } else {
        return null; // custom — caller handles
    }
    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
}

// ─── Base Sub-components ─────────────────────────────────────────────────────

export const SkeletonBlock = ({ className = '' }) => (
    <div className={`bg-[var(--color-border)] animate-pulse rounded-xl ${className}`} />
);

/** Scorecard widget — matches main dashboard KpiCard pattern across the system */
export const ScoreCard = ({ title, value, subtitle, subtitleBadge, icon: Icon, gradient, loading, trend }) => {
    const valStr = String(value ?? '—');
    const len = valStr.length;

    let textSizeClass = 'text-base min-[380px]:text-lg sm:text-2xl';
    if (len > 15) {
        textSizeClass = 'text-[11px] min-[380px]:text-xs sm:text-lg';
    } else if (len > 12) {
        textSizeClass = 'text-xs min-[380px]:text-sm sm:text-xl';
    } else if (len > 9) {
        textSizeClass = 'text-sm min-[380px]:text-base sm:text-2xl';
    }

    return (
        <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-xs p-3 sm:p-4 flex flex-col justify-between hover:shadow-md transition-all min-w-0">
            <div className="flex items-center justify-between gap-1.5 mb-1 sm:mb-2">
                <p className="text-[10px] sm:text-[11px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider leading-none truncate flex-1" title={title}>
                    {title}
                </p>
                <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl ${gradient} flex items-center justify-center flex-shrink-0 shadow-xs`}>
                    <Icon className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-white" />
                </div>
            </div>
            <div className="min-w-0">
                {loading ? (
                    <SkeletonBlock className="h-6 sm:h-7 w-20 mt-1" />
                ) : (
                    <p className={`${textSizeClass} font-black text-[var(--color-text-primary)] leading-tight tracking-tight break-words`} title={valStr}>
                        {valStr}
                    </p>
                )}
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {trend != null && !loading && (
                        <span className={`flex items-center gap-0.5 text-[10px] font-bold ${trend > 0 ? 'text-emerald-500' : trend < 0 ? 'text-rose-500' : 'text-[var(--color-text-muted)]'}`}>
                            {trend > 0 ? <ArrowUpRight size={11} /> : trend < 0 ? <ArrowDownRight size={11} /> : <Minus size={11} />}
                            {Math.abs(trend)}%
                        </span>
                    )}
                    {subtitleBadge && !loading && (
                        <span className="text-[9px] font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded-full">
                            {subtitleBadge}
                        </span>
                    )}
                    {subtitle && !loading && (
                        <p className="text-[9px] sm:text-[11px] text-[var(--color-text-muted)] font-medium truncate">{subtitle}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

/** Section container */
export const Section = ({ title, icon: Icon, iconColor = 'text-blue-500', children, action }) => (
    <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[var(--color-border-soft)] flex items-center justify-between">
            <div className="flex items-center gap-2">
                <Icon size={15} className={iconColor} />
                <h2 className="text-sm font-bold text-[var(--color-text-primary)]">{title}</h2>
            </div>
            {action}
        </div>
        <div className="p-4">{children}</div>
    </div>
);

/** Order Status Funnel — horizontal bar */
export const StatusFunnel = ({ funnel, loading }) => {
    const colorMap = {
        DELIVERED: 'bg-emerald-500', CONFIRMED: 'bg-blue-500',
        SUBMITTED: 'bg-indigo-400', NEW: 'bg-sky-500', NEW_ORDER: 'bg-sky-500',
        CREATED: 'bg-sky-500',
        ACKNOWLEDGED: 'bg-violet-400', PENDING_WAREHOUSE: 'bg-orange-400',
        DISPATCHED: 'bg-cyan-500', DESPATCHED: 'bg-cyan-500',
        OUT_FOR_DELIVERY: 'bg-teal-500', CANCELLED: 'bg-rose-500',
        REJECTED: 'bg-red-600', RETURNED: 'bg-amber-500',
        REQUEST_FOR_MODIFICATION: 'bg-amber-500', REVISED: 'bg-indigo-300',
        COMPLETED: 'bg-emerald-700',
    };

    if (loading) {
        return <div className="space-y-2">{[...Array(5)].map((_, i) => <SkeletonBlock key={i} className="h-8" />)}</div>;
    }

    if (!funnel?.length) {
        return <p className="text-sm text-[var(--color-text-muted)] text-center py-6">No order data in this period.</p>;
    }

    const totalCount = funnel.reduce((s, f) => s + (f.count || 0), 0);
    const sorted = [...funnel].sort((a, b) => (b.count || 0) - (a.count || 0));

    return (
        <div className="space-y-2.5">
            {sorted.map((item) => {
                const pct = totalCount > 0 ? ((item.count || 0) / totalCount) * 100 : 0;
                const color = colorMap[item.statusCode?.toUpperCase()] || 'bg-gray-400';
                return (
                    <div key={item.statusCode} className="flex items-center gap-3">
                        <span className="text-[11px] font-bold text-[var(--color-text-muted)] w-36 shrink-0 truncate capitalize">
                            {item.statusCode?.replace(/_/g, ' ').toLowerCase() ?? '—'}
                        </span>
                        <div className="flex-1 h-5 bg-[var(--color-bg-hover)] rounded-full overflow-hidden">
                            <div
                                className={`h-full ${color} rounded-full transition-all duration-700 flex items-center pl-2`}
                                style={{ width: `${Math.max(pct, 4)}%` }}
                            >
                                {pct > 15 && (
                                    <span className="text-[9px] font-black text-white">{formatNumber(item.count)}</span>
                                )}
                            </div>
                        </div>
                        {pct <= 15 && (
                            <span className="text-[11px] font-bold text-[var(--color-text-primary)] w-8 text-right shrink-0">
                                {formatNumber(item.count)}
                            </span>
                        )}
                        <span className="text-[10px] text-[var(--color-text-muted)] w-10 text-right shrink-0">
                            {pct.toFixed(1)}%
                        </span>
                    </div>
                );
            })}
            <div className="pt-2 border-t border-[var(--color-border-soft)] flex items-center justify-between">
                <span className="text-[11px] text-[var(--color-text-muted)] font-medium">Total orders in period</span>
                <span className="text-sm font-black text-[var(--color-text-primary)]">{formatNumber(totalCount)}</span>
            </div>
        </div>
    );
};

/** Top Customers table */
export const TopCustomersTable = ({ customers, loading }) => {
    if (loading) return <div className="space-y-2">{[...Array(5)].map((_, i) => <SkeletonBlock key={i} className="h-10" />)}</div>;
    if (!customers?.length) return <p className="text-sm text-[var(--color-text-muted)] text-center py-6">No customer data in this period.</p>;

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead>
                    <tr className="border-b border-[var(--color-border-soft)]">
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">#</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">Customer</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider text-center">Orders</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider text-right">Revenue</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border-soft)]">
                    {customers.map((c, idx) => (
                        <tr key={c.publicId} className="hover:bg-[var(--color-bg-hover)] transition-colors">
                            <td className="py-2.5 pr-3">
                                <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ${idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-slate-100 text-slate-600' : idx === 2 ? 'bg-orange-100 text-orange-700' : 'text-[var(--color-text-muted)]'}`}>
                                    {idx + 1}
                                </span>
                            </td>
                            <td className="py-2.5">
                                <div className="flex items-center gap-2">
                                    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${getAvatarGradient(c.name)} flex items-center justify-center text-white text-[10px] font-black shrink-0`}>
                                        {c.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <span className="text-sm font-semibold text-[var(--color-text-primary)] truncate max-w-[120px]">{c.name}</span>
                                </div>
                            </td>
                            <td className="py-2.5 text-center">
                                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{c.orderCount}</span>
                            </td>
                            <td className="py-2.5 text-right">
                                <span className="text-sm font-black text-emerald-600 tabular-nums">{formatCurrency(c.revenue)}</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/** Top Selling Products table */
export const TopProductsTable = ({ products, loading }) => {
    if (loading) return <div className="space-y-2">{[...Array(5)].map((_, i) => <SkeletonBlock key={i} className="h-10" />)}</div>;
    if (!products?.length) return <p className="text-sm text-[var(--color-text-muted)] text-center py-6">No product sales in this period.</p>;

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead>
                    <tr className="border-b border-[var(--color-border-soft)]">
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">#</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">Product</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider text-center">Qty Sold</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider text-right">Revenue</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border-soft)]">
                    {products.map((p, idx) => (
                        <tr key={p.publicId} className="hover:bg-[var(--color-bg-hover)] transition-colors">
                            <td className="py-2.5 pr-3">
                                <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ${idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-slate-100 text-slate-600' : idx === 2 ? 'bg-orange-100 text-orange-700' : 'text-[var(--color-text-muted)]'}`}>
                                    {idx + 1}
                                </span>
                            </td>
                            <td className="py-2.5">
                                <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center shrink-0">
                                        <Package size={12} className="text-white" />
                                    </div>
                                    <span className="text-sm font-semibold text-[var(--color-text-primary)] truncate max-w-[140px]">{p.name}</span>
                                </div>
                            </td>
                            <td className="py-2.5 text-center">
                                <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full tabular-nums">
                                    {formatNumber(p.quantitySold)}
                                </span>
                            </td>
                            <td className="py-2.5 text-right">
                                <span className="text-sm font-black text-emerald-600 tabular-nums">{formatCurrency(p.revenue)}</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/** Date Range Picker */
export const CustomDatePicker = ({ customStart, customEnd, onStartChange, onEndChange }) => (
    <CustomDatePickerComponent
        mode="range"
        startDate={customStart}
        endDate={customEnd}
        onStartChange={onStartChange}
        onEndChange={onEndChange}
    />
);

/** Customers to Call — expandable list */
export const CustomersToCallList = ({ customers, loading }) => {
    const [expanded, setExpanded] = useState(false);

    if (loading) return <SkeletonBlock className="h-16" />;

    if (!customers?.length) {
        return (
            <div className="flex items-center gap-3 py-3 px-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <Users size={14} className="text-emerald-600" />
                </div>
                <div>
                    <p className="text-sm font-bold text-emerald-800">All customers are active</p>
                    <p className="text-[11px] text-emerald-600 font-medium">No customers without a recent order.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl overflow-hidden">
            <button
                onClick={() => setExpanded(e => !e)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-rose-100 transition-colors"
            >
                <div className="w-9 h-9 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
                    <Users size={16} className="text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-rose-900">{customers.length} Customer{customers.length !== 1 ? 's' : ''} to Follow Up</p>
                    <p className="text-[11px] text-rose-700 font-medium mt-0.5">No order placed in the last 30 days</p>
                </div>
                <span className="text-xs font-bold text-rose-700 bg-rose-200 px-2.5 py-1 rounded-full shrink-0">{customers.length}</span>
                {expanded ? <ChevronUp size={16} className="text-rose-500 shrink-0" /> : <ChevronDown size={16} className="text-rose-500 shrink-0" />}
            </button>
            {expanded && (
                <div className="border-t border-rose-200 divide-y divide-rose-100">
                    {customers.map(c => (
                        <div key={c.publicId} className="flex items-center gap-3 px-4 py-3 hover:bg-rose-100/50 transition-colors">
                            <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(c.name)} flex items-center justify-center text-white text-[10px] font-black shrink-0`}>
                                {c.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-rose-900 truncate">{c.name}</p>
                                <p className="text-[10px] text-rose-600 font-medium">
                                    {c.lastOrderDate
                                        ? `Last order ${c.daysSinceLastOrder}d ago`
                                        : 'No orders placed yet'}
                                </p>
                            </div>
                            <div className="shrink-0">
                                <span className={`text-[10px] font-black px-2 py-1 rounded-full ${c.daysSinceLastOrder > 60 || !c.lastOrderDate ? 'bg-red-100 text-red-700' : 'bg-rose-100 text-rose-700'}`}>
                                    {c.daysSinceLastOrder > 0 ? `${c.daysSinceLastOrder}d` : 'Never'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
